import React, { useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import Heading from 'LuxKit/Typography/Heading'
import PayAtPropertyPill from 'components/Common/PayAtPropertyPill'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import BedbankCancellationPolicy from '../BedbankCancellationPolicy'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import Plural from 'components/utils/Formatters/Plural'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import CurrencyContext from 'contexts/currencyContext'
import Sheet from 'components/utils/Sheet'
import { mediaQueryOnly } from 'components/utils/breakpoint'

const PaymentOptionsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  padding-top: 1rem;
  
  ${mediaQueryOnly.mobile} {
    grid-template-columns: 1fr;
  }
`

const DescriptionText = styled(BodyText)`
  min-height: ${rem(60)}; // Approximately 3 lines of text
`

function BedbankPayAtPropertyModal({ payAtPropertyRate, lePaymentNowRate }: {
  payAtPropertyRate: App.BedbankRate;
  lePaymentNowRate: App.BedbankRate;
}) {
  const { resolve } = useModalElementContext<App.BedbankRate | undefined>()
  const contextCurrency = useContext(CurrencyContext)

  const onSelectPayAtProperty = () => {
    resolve(payAtPropertyRate)
  }

  const onSelectPayNow = () => {
    resolve(lePaymentNowRate)
  }

  const onClose = () => {
    resolve(undefined)
  }

  const payAtPropertyTotal =
    (payAtPropertyRate.totals.inclusive ?? 0) +
    (payAtPropertyRate.totals.propertyFees ?? 0)

  const payNowTotal =
    (lePaymentNowRate.totals.inclusive ?? 0) +
    (lePaymentNowRate.totals.propertyFees ?? 0)

  return (
    <ModalBase size="L">
      <ModalHeader title="Your payment options" onCloseButtonClick={onClose} />
      <ModalBody>
        <ModalContent>
          <PaymentOptionsContainer>
            {/* Pay at Property Option */}
            <Sheet
              border="neutral-five"
              borderRadius="S"
              padding={20}
            >
              <VerticalSpacer gap={12}>
                <Group
                  direction="horizontal"
                  gap={8}
                  horizontalAlign="space-between"
                >
                  <Heading variant="heading4">Pay $0 now</Heading>
                  <PayAtPropertyPill />
                </Group>

                <DescriptionText variant="medium" colour="neutral-two">
                  Prices in {contextCurrency} are estimated, you will pay in the
                  property's preferred currency (USD 8,560)
                </DescriptionText>

                <BedbankCancellationPolicy
                  rate={payAtPropertyRate}
                  showNonCancellation
                  showHotelOnly={false}
                />

                <Group direction="vertical" gap={2}>
                  <PriceRowPriceCaption>
                    <Plural count={payAtPropertyRate.nights} singular="night" withCount />
                  </PriceRowPriceCaption>

                  <PriceRowPrice price={payAtPropertyTotal} saleUnit="total" size="L" />

                  <PriceRowTaxesAndFees />
                </Group>

                <TextButton
                  size="large"
                  kind="primary"
                  fit="full-width"
                  onClick={onSelectPayAtProperty}
                >
                  Pay at property
                </TextButton>
              </VerticalSpacer>
            </Sheet>

            {/* Pay Now Option */}
            <Sheet
              border="neutral-five"
              borderRadius="S"
              padding={20}
            >
              <VerticalSpacer gap={12}>
                <Heading variant="heading4">Pay in full now</Heading>

                <DescriptionText variant="medium" colour="neutral-two">
                  We process your payment now in your local currency
                </DescriptionText>

                <BedbankCancellationPolicy
                  rate={lePaymentNowRate}
                  showNonCancellation
                  showHotelOnly={false}
                />

                <Group direction="vertical" gap={2}>
                  <PriceRowPriceCaption>
                    <Plural count={lePaymentNowRate.nights} singular="night" withCount />
                  </PriceRowPriceCaption>
                  <PriceRowPrice price={payNowTotal} saleUnit="total" size="L" />
                  <PriceRowTaxesAndFees />
                </Group>

                <TextButton
                  size="large"
                  kind="primary"
                  fit="full-width"
                  onClick={onSelectPayNow}
                >
                  Pay now
                </TextButton>
              </VerticalSpacer>
            </Sheet>
          </PaymentOptionsContainer>
        </ModalContent>
      </ModalBody>
    </ModalBase>
  )
}

export default BedbankPayAtPropertyModal
